/**
 * 共通API呼び出しユーティリティ
 *
 * @file api.ts
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * ネットワークエラー・APIエラーを統一的に処理するsafeFetch関数を提供。
 *
 * 使用例:
 *
 * // 基本的な使用
 * const products = await safeFetch<ProductListResponse>(
 *   `${API_BASE_URL}/products`,
 *   { errorMessage: '商品一覧の取得に失敗しました。' }
 * );
 *
 * // GETヘルパー使用
 * const user = await safeFetchGet<User>(
 *   `${API_BASE_URL}/users/me`,
 *   { Authorization: `Bearer ${token}` },
 *   'ユーザー情報の取得に失敗しました。'
 * );
 *
 * // POSTヘルパー使用
 * const result = await safeFetchPost<CreateProductResponse>(
 *   `${API_BASE_URL}/products`,
 *   { name: '商品名', price: 1000 },
 *   { Authorization: `Bearer ${token}` },
 *   '商品の作成に失敗しました。'
 * );
 */

import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { errorHandler, ErrorCategory, ErrorSeverity } from './errorHandler';
import { handleUnauthorized } from './authRedirect';

export interface SafeFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: HeadersInit;
  body?: unknown;
  errorMessage: string;
  timeout?: number;
  signal?: AbortSignal;
}

/**
 * バックエンドAPIエラーレスポンスの想定形状
 * FastAPIの生HTTPException（{ detail }）と、独自エラーエンベロープ（{ error: {...} }）の
 * 両方をカバーする。フィールドは全て未確定のため任意（Optional）として扱う。
 */
interface ApiErrorResponseBody {
  detail?: string;
  message?: string;
  error?: {
    code?: string;
    message?: string;
    translationKey?: string;
    details?: Array<{ loc: Array<string | number>; msg: string; type: string }>;
  };
}

export class ApiValidationError extends Error {
  status: number;
  code: string;
  translationKey: string | undefined;
  details: Array<{ loc: Array<string | number>; msg: string; type: string }>;

  constructor(message: string, status: number, code: string, translationKey: string | undefined, details: Array<{ loc: Array<string | number>; msg: string; type: string }>) {
    super(message);
    this.name = 'ApiValidationError';
    this.status = status;
    this.code = code;
    this.translationKey = translationKey;
    this.details = details;
  }
}

/**
 * 安全なfetch実行（エラーハンドリング統合）
 * axios基盤で実装、withCredentials: true統一
 *
 * @param url - リクエストURL
 * @param options - axios options + エラーメッセージ
 * @returns レスポンスJSON
 * @throws ネットワークエラー、APIエラー
 */
export async function safeFetch<T>(
  url: string,
  options: SafeFetchOptions
): Promise<T> {
  const { errorMessage: customErrorMessage, timeout = 30000, method = 'GET', headers, body, signal } = options;

  try {
    // HeadersInitをRecord<string, string>に変換
    let axiosHeaders: Record<string, string> | undefined;
    if (headers) {
      if (headers instanceof Headers) {
        axiosHeaders = {};
        headers.forEach((value, key) => {
          axiosHeaders![key] = value;
        });
      } else if (Array.isArray(headers)) {
        axiosHeaders = {};
        headers.forEach(([key, value]) => {
          axiosHeaders![key] = value;
        });
      } else {
        axiosHeaders = headers as Record<string, string>;
      }
    }

    const axiosConfig: AxiosRequestConfig = {
      method,
      url,
      headers: axiosHeaders,
      data: body,
      timeout,
      withCredentials: true,
      signal,
    };

    const response = await axios(axiosConfig);
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      // タイムアウトエラー
      if (axiosError.code === 'ECONNABORTED') {
        const timeoutError = new Error('リクエストがタイムアウトしました。');
        errorHandler.handleError(
          timeoutError,
          ErrorCategory.NETWORK,
          ErrorSeverity.MEDIUM,
          { url, timeout }
        );
        throw timeoutError;
      }

      // ネットワークエラー（サーバーに到達できない）
      if (!axiosError.response) {
        const networkError = new Error(customErrorMessage);
        errorHandler.handleNetworkError(networkError, url);
        throw networkError;
      }

      // APIエラー（サーバーからエラーレスポンス）
      const status = axiosError.response.status;

      // 401 Unauthorized: セッション切れ -> ログイン画面へリダイレクト
      if (status === 401) {
        handleUnauthorized(url);
      }

      const responseData = axiosError.response.data as ApiErrorResponseBody;
      const errorCode = axiosError.response.headers['x-error-code'];

      // 422 Unprocessable Entity: 構造化バリデーションエラーをスロー
      if (status === 422 && responseData?.error?.details && Array.isArray(responseData.error.details)) {
        throw new ApiValidationError(
          responseData.error.message || `API Error: ${status}`,
          status,
          responseData.error.code || 'VALIDATION_ERROR',
          responseData.error.translationKey,
          responseData.error.details
        );
      }

      const errorMessage = responseData?.detail
        || responseData?.message
        || `API Error: ${status}`;
      const apiError = new Error(errorMessage) as Error & { status?: number; code?: string };
      apiError.status = status;
      apiError.code = responseData?.error?.code;

      errorHandler.handleError(
        apiError,
        ErrorCategory.NETWORK,
        status >= 500 ? ErrorSeverity.HIGH : ErrorSeverity.MEDIUM,
        { url, status, errorCode, detail: errorMessage, response: responseData }
      );

      throw apiError;
    }

    // その他のエラー
    throw error;
  }
}

/**
 * GET リクエスト用ヘルパー
 */
export async function safeFetchGet<T>(
  url: string,
  headers: HeadersInit | undefined,
  errorMessage: string
): Promise<T> {
  return safeFetch<T>(url, {
    method: 'GET',
    headers,
    errorMessage
  });
}

/**
 * POST リクエスト用ヘルパー
 */
export async function safeFetchPost<T>(
  url: string,
  body: unknown,
  headers: HeadersInit | undefined,
  errorMessage: string
): Promise<T> {
  return safeFetch<T>(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(typeof headers === 'object' && !Array.isArray(headers) && !(headers instanceof Headers)
        ? headers
        : {})
    },
    body,
    errorMessage
  });
}
